/// <amd-module name="VSS/Platform/Page" />
import { IVssPageContext, IVssService, Services, TeamFoundationHostType, VssService } from "./Context";
import { ClientContribution as Contribution } from "./Contracts/Contributions";
import { IdentityRef } from "./Contracts/WebApi";
import { IVssContributionService } from "./Contribution";

/*
 * @TODO: Do we want to move ALL diagnostic data definition to a single place.
 *  This could include, performance, and any other diagnostics we want to return
 *  from the server. We could make it a top-level returned object, instead of
 *  a data provider value.
 */

/**
 * Diagnostic data is sent from the server when the caller requests diagnostic data
 * be produced. Some diagnostic data MAY be available by default by the more
 * expensive and heavily detailed data used for debugging problems in the page are
 * not included.
 */
export interface IDiagnosticData {
    /**
     * Contrituion Diagnostic data is NOT included by default. The contribution
     * data is used to define the details about which contributions are included
     * in the page along with all other contributions that were excluded but were
     * evaluated in the process of executing the page.
     *
     * The data will include all the contributions, the relationships between the
     * contributions, any conditions that were evaluated to determine their
     * inclusion in the page.
     *
     * Each query the server executes is named and some queries my run more than
     * once. The contributions value makes the entire set of named queries available
     * to the client.
     */
    contributions?: { [name: string]: IContributionDiagnostics[] };
}

/**
 * Each query executed on the server produces an IContributionDiagnostics record.
 * This is the set of contributions reached and the relationships that were used
 * to traverse the hierarchy.
 */
export interface IContributionDiagnostics {
    /**
     * This is the entire set of contributions encountered during the query.
     */
    contributions: { [contributionId: string]: Contribution };

    /**
     * This is the set of relationships used to traverse the hierachy. It includes
     * all the conditions their evaluations.
     */
    relationships: { [parentId: string]: IEvaluatedRelationship[] };
}

/**
 * A contribution relationship is defined by the targets and includes defined
 * in the contribution hierarchy. When a query is executed the query traverses
 * the relationships and records information about the state of the query for
 * each parent/child pair it encounters. Only the first time the relationship is
 * encountered is the relationship evaluated since this can occur more than once.
 */
export interface IEvaluatedRelationship {
    /**
     * Child contribution of the relationship.
     */
    childContributionId: string;

    /**
     * Parent contribution of the relationship.
     */
    parentContributionId: string;

    /**
     * The requested filtering options passed into the evaluation.
     */
    inputOptions: number;

    /**
     * The resulting filtering options returned from the evaluation.
     */
    outputOptions: number;

    /**
     * The set of explicit conditions that were evaluated for this relationship.
     */
    conditions?: IEvaluatedCondition[];
}

/**
 * Each relationship may have 0 to N conditions applied to it. These come in the
 * form of data defined as constraints along with filtering functions that are
 * supplied to the query itself.
 */
export interface IEvaluatedCondition {
    /**
     * The result of the evaluation. Did the condition pass or not. Some
     * conditions may be applied and not make a decision, while others may
     * determine the filtering state directly.
     */
    evaluation: boolean;

    /**
     * Filtered is set to true if this condition filtered the contribution out
     * of the result.
     */
    filtered: boolean;

    /**
     * The evaluation can chose to append details to the evaluation that describe
     * the condition that was evaluated. The shape of the details are left up to
     * the code that performs the evaluation.
     */
    details?: any;
}

/**
 * This is the basic data sent to the client that describes the state of the page being
 * executed. It has basic information about the service and the entities accessed.
 */
export interface IPageData {
    /**
     * False if esnext scripts are definitely not supported. True otherwise.
     */
    esNextSupported: boolean;

    /**
     * UniqueId for the Host being accessed.
     */
    hostId: string;

    /**
     * This is the TeamFoundationHostType value for the current host.
     */
    hostType: TeamFoundationHostType;

    /**
     * Display name of the host for the current request.
     */
    hostName: string;

    /**
     * The root path for the Host being accessed.
     */
    hostPath: string;

    /**
     * The service instance type id of the service that served the page.
     */
    serviceInstanceType: string;

    /**
     * Defines whether or not content bundling is currently enabled.
     */
    isBundlingEnabled: boolean;

    /**
     * Defines whether or not the CDN is configured and available in this environment.
     */
    isCdnAvailable: boolean;

    /**
     * Defines whether or not cdn is being used.
     */
    isCdnEnabled: boolean;

    /**
     * Defines whether or not the page is being created in debug mode.
     */
    isDebugMode: boolean;

    /**
     * Defines whether or not the page is coming from a devfabric environment.
     */
    isDevfabric: boolean;

    /**
     * Defines whether or not the page is coming from a Hosted or OnPrem server.
     */
    isHosted: boolean;

    /**
     * Defines whether or not the page is coming from a Production of Dev environment.
     */
    isProduction: boolean;

    /**
     * Returns true if only SSL endpoint should process traffic.
     */
    isSslOnly: boolean;

    /**
     * True if trace-point collection is currently enabled (turns on the Perf Bar)
     */
    isTracePointCollectionEnabled: boolean;

    /**
     * String representing the script language used on this page. Either
     * "javascript" (es5) or "ES2017"
     */
    scriptType: string;

    /**
     * A unique sessionId for this this page. Ajax requests being made on-behalf of this
     * page should tag the request with this sessionId to associate it with this page.
     */
    sessionId: string;

    /**
     * The milestone and patch version of the tfs service.
     */
    serviceVersion: string;

    /**
     * Each request will have some basic information about the user that made the request.
     */
    user: IdentityRef;

    /**
     * Current web platform version. Can be used to determine that we need to reload
     * the page on FPS.
     */
    webPlatformVersion: number;

    /**
     * Depending on the state of the request, there may be diagnostic data returned from
     * the server that describes how the page was produced.
     */
    diagnostics?: IDiagnosticData;
}

export interface IVssPageService extends IVssService {
    getData(): IPageData | undefined;
}

class VssPageService extends VssService implements IVssPageService {
    public getData(): IPageData | undefined {
        const contributionService = this.pageContext.getService<IVssContributionService>("IVssContributionService");
        return contributionService.getData<IPageData>("ms.vss-web.page-data");
    }
}

Services.add("IVssPageService", { serviceFactory: VssPageService });
